// ===========================================
// Student Pieces Card
// ===========================================
// Shows pieces assigned to a student's classes
// Grouped into "Currently Learning" and "Past Pieces"

import { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Divider,
  Skeleton,
  Alert,
  Chip,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  MusicNote as MusicNoteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useStudentLessonPieces } from '../../../hooks/useGamification';
import StageProgressBar from '../../gamification/StageProgressBar';
import type { StageSegment } from '../../gamification/StageProgressBar';
import type { StudentLessonPieceItem, SkillStatus } from '../../../types/gamification.types';

interface StudentPiecesCardProps {
  studentId: string;
  studentName?: string;
  /** Fun mode styling for student dashboard */
  isFunMode?: boolean;
  /** Card styling overrides */
  cardSx?: Record<string, unknown>;
  /** Title styling overrides */
  titleSx?: Record<string, unknown>;
  /** Divider color for fun mode */
  dividerColor?: string;
  /** Avatar image URL to show on the progress bar */
  avatarSrc?: string;
  /** Filter pieces to only show those from specific lesson IDs */
  filterLessonIds?: string[];
}

export default function StudentPiecesCard({
  studentId,
  studentName,
  isFunMode = false,
  cardSx,
  titleSx,
  dividerColor,
  avatarSrc,
  filterLessonIds,
}: StudentPiecesCardProps) {
  const [showPast, setShowPast] = useState(false);
  const { data, isLoading } = useStudentLessonPieces(studentId);

  const allCurrentPieces = data?.currentPieces || [];
  const allPastPieces = data?.pastPieces || [];
  const currentPieces = filterLessonIds
    ? allCurrentPieces.filter((item) => filterLessonIds.includes(item.lesson.id))
    : allCurrentPieces;
  const pastPieces = filterLessonIds
    ? allPastPieces.filter((item) => filterLessonIds.includes(item.lesson.id))
    : allPastPieces;
  const hasPieces = currentPieces.length > 0 || pastPieces.length > 0;

  return (
    <Card sx={cardSx}>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={titleSx}>
          <MusicNoteIcon
            sx={{
              mr: 1,
              verticalAlign: 'middle',
              color: isFunMode && dividerColor ? dividerColor : 'primary.main',
            }}
          />
          {studentName ? `${studentName}'s Pieces` : 'My Pieces'}
        </Typography>
        <Divider
          sx={{
            mb: 2,
            borderColor: isFunMode && dividerColor ? `${dividerColor}40` : 'divider',
          }}
        />

        {isLoading ? (
          <Box>
            <Skeleton variant="rectangular" height={60} sx={{ mb: 1, borderRadius: 1 }} />
            <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 1 }} />
          </Box>
        ) : !hasPieces ? (
          <Alert severity="info" sx={isFunMode ? { borderRadius: 2 } : {}}>
            No pieces assigned to {studentName ? `${studentName}'s` : 'your'} classes yet.
          </Alert>
        ) : (
          <>
            {/* Currently Learning */}
            {currentPieces.length > 0 && (
              <>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Currently Learning ({currentPieces.length})
                </Typography>
                <List dense disablePadding>
                  {currentPieces.map((item) => (
                    <PieceItem key={item.lessonPiece.id} item={item} isFunMode={isFunMode} avatarSrc={avatarSrc} />
                  ))}
                </List>
              </>
            )}

            {/* Past Pieces (collapsible) */}
            {pastPieces.length > 0 && (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mt: currentPieces.length > 0 ? 2 : 0,
                    cursor: 'pointer',
                  }}
                  onClick={() => setShowPast(!showPast)}
                >
                  <Typography variant="subtitle2" color="text.secondary">
                    Past Pieces ({pastPieces.length})
                  </Typography>
                  <IconButton size="small">
                    {showPast ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>
                <Collapse in={showPast}>
                  <List dense disablePadding>
                    {pastPieces.map((item) => (
                      <PieceItem key={item.lessonPiece.id} item={item} isFunMode={isFunMode} />
                    ))}
                  </List>
                </Collapse>
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ===========================================
// PIECE ITEM SUB-COMPONENT
// ===========================================

function PieceItem({ item, isFunMode, avatarSrc }: { item: StudentLessonPieceItem; isFunMode: boolean; avatarSrc?: string }) {
  const { piece, studentPiece, lesson } = item;

  // Calculate progress
  const totalSkills = studentPiece?.skillProgress?.length || piece.skills?.length || 0;
  const completedSkills = studentPiece?.skillProgress?.filter((s) => s.status === 'COMPLETED').length || 0;
  const progressPercent = totalSkills > 0 ? Math.round((completedSkills / totalSkills) * 100) : 0;

  // Use the current in-progress skill's color as the border color
  const sortedSkills = [...(piece.skills || [])].sort((a, b) => a.sortOrder - b.sortOrder);
  const progressMap = new Map(
    (studentPiece?.skillProgress || []).map((sp) => [sp.pieceSkill.id, sp.status])
  );
  const inProgressSkill = sortedSkills.find((s) => progressMap.get(s.id) === 'IN_PROGRESS');
  // If all skills completed, use the last skill's color; otherwise fall back through the chain
  const allCompleted = sortedSkills.length > 0 && sortedSkills.every((s) => progressMap.get(s.id) === 'COMPLETED');
  const currentSkill = allCompleted
    ? sortedSkills[sortedSkills.length - 1]
    : inProgressSkill ||
      sortedSkills.find((s) => progressMap.get(s.id) === 'NOT_STARTED') ||
      sortedSkills[0];
  const pieceColor = currentSkill?.color || '#4580E4';

  return (
    <ListItem
      sx={{
        border: '2px solid',
        borderColor: pieceColor,
        borderRadius: isFunMode ? 2 : 1,
        mb: 0.5,
        flexDirection: 'column',
        alignItems: 'stretch',
        bgcolor: '#fff',
      }}
    >
      <ListItemText
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2" fontWeight={600}>
              {piece.title}
            </Typography>
            {studentPiece?.isCompleted && (
              <CheckCircleIcon color="success" sx={{ fontSize: 16 }} />
            )}
          </Box>
        }
        secondary={
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.25 }}>
            {piece.composer && (
              <Typography variant="caption" color="text.secondary">
                {piece.composer}
              </Typography>
            )}
            {lesson.name && (
              <Chip
                label={lesson.name}
                size="small"
                variant="outlined"
                sx={{ height: 18, fontSize: '0.65rem' }}
              />
            )}
          </Box>
        }
      />
      {/* Stage progress bar */}
      {totalSkills > 0 && (
        <Box sx={{ mt: avatarSrc ? 1.5 : 0.5, mb: 0.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
            <Typography variant="caption" color="text.secondary">
              {completedSkills}/{totalSkills} skills
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {progressPercent}%
            </Typography>
          </Box>
          <StageProgressBar
            stages={buildStages(piece, studentPiece)}
            size="small"
            avatarSrc={avatarSrc}
          />
        </Box>
      )}
    </ListItem>
  );
}

// ===========================================
// HELPERS
// ===========================================

function buildStages(
  piece: StudentLessonPieceItem['piece'],
  studentPiece: StudentLessonPieceItem['studentPiece']
): StageSegment[] {
  const skills = piece.skills || [];
  const progressMap = new Map(
    (studentPiece?.skillProgress || []).map((sp) => [sp.pieceSkill.id, sp])
  );

  return [...skills]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((skill) => {
      const progress = progressMap.get(skill.id);
      return {
        id: skill.id,
        name: skill.name,
        color: skill.color,
        status: (progress?.status || 'NOT_STARTED') as SkillStatus,
      };
    });
}
